/**
 * ActionItemView — one editable action item.
 *
 * Phase 6: description is contenteditable text*; assignee + due
 * date stay as typed attrs and render as inline pills via small
 * popovers. Status marker click cycles open → done → cancelled.
 *
 * Wave-1 cleanup:
 *   - Pill popovers now share the `usePopover` hook, which fixes
 *     the stale-closure data-loss bug (commit one would persist
 *     a stale `draft` if the underlying value changed while the
 *     popover was open) and adds focus restoration + Escape
 *     handling for free.
 *   - `formatShortDate` consolidated into the shared `dateFormat`
 *     module so MeetingHeaderView + here use the exact same
 *     parser.
 *   - Status marker now responds to Space / Enter as well as
 *     mouse clicks — keyboard users can cycle status without
 *     reaching for the pointer.
 */
import { useEffect, useRef, useState } from 'react';
import {
  NodeViewContent,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { X, Calendar, User } from 'lucide-react';
import { ReorderControls } from './ReorderControls.tsx';
import { usePopover } from '../usePopover.ts';
import { formatShortDate } from '../dateFormat.ts';

interface ActionAttrs {
  assigneeName?: string | null;
  dueDate?: string | null;
  status?: 'open' | 'done' | 'cancelled';
}

const STATUS_CYCLE: Record<NonNullable<ActionAttrs['status']>, ActionAttrs['status']> = {
  open: 'done',
  done: 'cancelled',
  cancelled: 'open',
};

function statusMarker(status: ActionAttrs['status']) {
  if (status === 'done') return { glyph: '✓', className: 'text-pine' };
  if (status === 'cancelled') return { glyph: '×', className: 'text-charcoal' };
  return { glyph: '+', className: 'text-pine' };
}

export function ActionItemView({
  node,
  updateAttributes,
  editor,
  getPos,
}: NodeViewProps) {
  const attrs = node.attrs as ActionAttrs;
  const status = attrs.status ?? 'open';
  const marker = statusMarker(status);
  const isDone = status === 'done';
  const isCancelled = status === 'cancelled';
  const isEditable = editor.isEditable;

  const cycleStatus = () => {
    updateAttributes({ status: STATUS_CYCLE[status] });
  };

  const removeItem = () => {
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (pos === null) return;
    editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + node.nodeSize })
      .run();
  };

  return (
    <NodeViewWrapper
      as="li"
      className="tt-action-item group/row relative flex items-start gap-2 text-[12px] leading-relaxed"
      data-node="actionItem"
    >
      {/* Reorder gutter — up arrow + drag handle + down arrow,
          rendered in the page's left margin via absolute
          positioning. Inherits hover state via group/row on the
          wrapper. */}
      <ReorderControls
        editor={editor}
        getPos={getPos as () => number | undefined}
        label="action item"
      />

      {/* Status marker — clickable to cycle. Space / Enter also
          cycle so keyboard-only users (and screen readers) can
          access the affordance. aria-label calls out the current
          state + the action so AT users hear "open, click to
          cycle status" rather than just a glyph. */}
      {isEditable ? (
        <button
          type="button"
          contentEditable={false}
          onClick={cycleStatus}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              cycleStatus();
            }
          }}
          title={`Status: ${status} (click or press Space to cycle)`}
          aria-label={`Action item status: ${status}. Press to cycle.`}
          className={
            'mt-[1px] shrink-0 select-none font-bold transition-colors hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine/40 focus-visible:rounded-sm ' +
            marker.className
          }
        >
          {marker.glyph}
        </button>
      ) : (
        <span
          className={'mt-[1px] shrink-0 font-bold ' + marker.className}
        >
          {marker.glyph}
        </span>
      )}

      <div className="flex-1">
        {/* Editable description — inline contenteditable. Marks
            apply, BubbleMenu shows on selection, etc.
            draggable={false} so drag-to-reorder (schema flag) is
            initiated only from the action item's non-text area
            and doesn't interfere with text selection inside. */}
        <NodeViewContent
          as="span"
          className={
            'tt-action-item__description text-charcoal ' +
            (isDone ? 'line-through opacity-60 ' : '') +
            (isCancelled ? 'opacity-50 ' : '')
          }
          draggable={false}
        />

        {/* Read-only render: assignee + due render INLINE after the
            description as " — Name (due Date)" to match the .docx
            and .pdf output format exactly. WYSIWYG with the export.
            Pilot decision: visible parity > pill affordance. */}
        {!isEditable && (attrs.assigneeName || attrs.dueDate) && (
          <span
            contentEditable={false}
            className="select-none text-charcoal/55"
          >
            {attrs.assigneeName ? (
              <span> - {attrs.assigneeName}</span>
            ) : null}
            {attrs.dueDate ? (
              <span> (due {formatShortDate(attrs.dueDate)})</span>
            ) : null}
          </span>
        )}

        {/* Editable mode keeps the pill row so users can click to
            edit. The pills are visually denser than the inline
            export format but they're the affordance — switching
            this section over to inline-edit-by-click is a polish
            follow-up if pilot users find the visual mismatch
            jarring during editing. */}
        {isEditable && (
          <div
            contentEditable={false}
            className="ml-1 mt-0.5 inline-flex flex-wrap items-center gap-1 align-baseline text-[10px]"
          >
            <AssigneePillButton
              value={attrs.assigneeName ?? ''}
              isEditable={isEditable}
              onChange={(next) =>
                updateAttributes({ assigneeName: next || null })
              }
            />
            <DueDatePillButton
              value={attrs.dueDate ?? ''}
              isEditable={isEditable}
              onChange={(next) => updateAttributes({ dueDate: next || null })}
            />
            <button
              type="button"
              onClick={removeItem}
              title="Remove action item"
              aria-label="Remove action item"
              className="ml-auto select-none text-charcoal opacity-0 transition-opacity hover:text-pine group-hover/row:opacity-100"
            >
              <X size={11} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}

// ─── Pill primitives ─────────────────────────────────────────────────

function pillClass(filled: boolean) {
  return (
    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors ' +
    (filled
      ? 'border-pine/30 bg-pine/10 text-pine '
      : 'border-charcoal/20 text-charcoal ') +
    'hover:border-pine hover:text-pine'
  );
}

function AssigneePillButton({
  value,
  isEditable,
  onChange,
}: {
  value: string;
  isEditable: boolean;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  // Keep draft in sync with the prop whenever the parent updates
  // the underlying value (autosave, AI rewrite, drag-rebuild).
  useEffect(() => setDraft(value), [value]);

  // Mirror draft into a ref so usePopover's onCommit always sees
  // the latest value, even if the React closure was stale at the
  // time the listener was registered.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const valueRef = useRef(value);
  valueRef.current = value;

  const popover = usePopover<HTMLSpanElement>({
    onCommit: () => {
      if (draftRef.current !== valueRef.current) onChange(draftRef.current);
    },
  });

  if (!isEditable && !value) return null;

  return (
    <span ref={popover.wrapperRef} className="relative inline-block">
      <button
        type="button"
        disabled={!isEditable}
        onClick={popover.toggle}
        className={pillClass(Boolean(value))}
      >
        <User size={10} strokeWidth={2.5} />
        <span>{value || 'Owner'}</span>
      </button>
      {popover.isOpen && isEditable && (
        <span className="absolute left-0 top-full z-30 mt-1 block w-48 rounded-lg border border-charcoal/20 bg-white p-2 shadow-xl">
          <input
            type="text"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                popover.close();
              }
              // Escape is handled by usePopover's keydown listener
              // (so other floating elements don't ALSO close on
              // the same Escape).
            }}
            placeholder="Owner name"
            className="w-full rounded border border-charcoal/25 px-2 py-1 text-[12px] focus:border-pine focus:outline-none"
          />
        </span>
      )}
    </span>
  );
}

function DueDatePillButton({
  value,
  isEditable,
  onChange,
}: {
  value: string;
  isEditable: boolean;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const valueRef = useRef(value);
  valueRef.current = value;

  const popover = usePopover<HTMLSpanElement>({
    onCommit: () => {
      if (draftRef.current !== valueRef.current) onChange(draftRef.current);
    },
  });

  if (!isEditable && !value) return null;

  return (
    <span ref={popover.wrapperRef} className="relative inline-block">
      <button
        type="button"
        disabled={!isEditable}
        onClick={popover.toggle}
        className={pillClass(Boolean(value))}
      >
        <Calendar size={10} strokeWidth={2.5} />
        <span>{value ? `Due ${formatShortDate(value)}` : 'Due'}</span>
      </button>
      {popover.isOpen && isEditable && (
        <span className="absolute left-0 top-full z-30 mt-1 block rounded-lg border border-charcoal/20 bg-white p-2 shadow-xl">
          <input
            type="date"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                popover.close();
              }
            }}
            className="rounded border border-charcoal/25 px-2 py-1 text-[12px] focus:border-pine focus:outline-none"
          />
        </span>
      )}
    </span>
  );
}
